import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { inr } from "../lib/api";
import { Printer, Download, ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Standalone print-optimized invoice page.
 * URL: /invoice/:orderId?autoprint=1
 * - Renders outside the main Layout (no nav/footer) for clean printing.
 * - `@media print` styles hide toolbar and force A4-friendly layout.
 * - `?autoprint=1` opens the browser print dialog automatically, letting the user
 *   either print physically or "Save as PDF" (native browser download).
 */
export default function InvoicePage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load invoice");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  // Auto-open the print dialog once loaded (used by Print/Download buttons)
  useEffect(() => {
    if (!loading && order && params.get("autoprint") === "1") {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, order, params]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-welljio-muted">
        <Loader2 className="animate-spin mr-2" size={18} /> Preparing invoice…
      </div>
    );
  }
  if (error || !order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6">
        <h1 className="font-heading text-2xl font-semibold">{error || "Invoice not found"}</h1>
        <Link to="/" className="text-welljio-accent hover:underline">Back to home</Link>
      </div>
    );
  }

  const items = order.items || [];
  const addr = order.shipping_address || {};
  const invoiceNo = `INV-${order.id.slice(-10).toUpperCase()}`;
  const invoiceDate = new Date(order.created_at).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const paymentLabel = order.payment_method === "cod"
    ? "Cash on Delivery"
    : order.status === "paid" ? "Razorpay (Paid)" : "Razorpay (Pending)";

  return (
    <div className="min-h-screen bg-welljio-tint/40 py-8 print:bg-white print:py-0">
      {/* Toolbar — hidden on print */}
      <div className="max-w-4xl mx-auto px-6 mb-6 flex items-center justify-between print:hidden">
        <Link to="/account" data-testid="invoice-back" className="inline-flex items-center gap-2 text-sm text-welljio-muted hover:text-welljio-ink transition-colors">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="invoice-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full border border-welljio-ink/15 bg-white px-5 py-2.5 text-sm font-medium hover:bg-welljio-tint transition-colors"
          >
            <Printer size={16} /> Print
          </button>
          <button
            type="button"
            data-testid="invoice-download-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full bg-welljio-accent text-white px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Download size={16} /> Download PDF
          </button>
        </div>
      </div>

      {/* Invoice sheet */}
      <div
        data-testid="invoice-sheet"
        className="max-w-4xl mx-auto bg-white shadow-xl print:shadow-none border border-slate-100 print:border-0 rounded-2xl print:rounded-none overflow-hidden"
      >
        {/* Header */}
        <div className="px-10 pt-10 pb-8 border-b border-slate-100 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <img src="/welljio-logo.png" alt="Welljio" className="h-10 w-auto mb-4" />
            <p className="text-xs text-welljio-muted leading-relaxed">
              Anaya Medical Systems Pvt. Ltd.<br />
              Hyderabad, Telangana, India<br />
              support@welljio.com · +91 80 4567 8910<br />
              GSTIN: 29ABCDE1234F1Z5
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle font-semibold mb-1">Tax Invoice</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-welljio-ink" data-testid="invoice-number">{invoiceNo}</h1>
            <div className="mt-3 space-y-1 text-xs text-welljio-muted">
              <p><span className="text-welljio-subtle uppercase tracking-wide">Order</span> #{order.id.slice(-10)}</p>
              <p><span className="text-welljio-subtle uppercase tracking-wide">Date</span> {invoiceDate}</p>
            </div>
          </div>
        </div>

        {/* Bill To / Ship To */}
        <div className="px-10 py-8 grid sm:grid-cols-2 gap-8 border-b border-slate-100">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle font-semibold mb-3">Billed To</p>
            <p className="font-semibold text-welljio-ink">{order.customer?.name || "—"}</p>
            <p className="text-sm text-welljio-muted mt-1">{order.customer?.email}</p>
            {order.customer?.phone && <p className="text-sm text-welljio-muted">{order.customer.phone}</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-welljio-subtle font-semibold mb-3">Ship To</p>
            {(addr.line1 || addr.city) ? (
              <address className="not-italic text-sm text-welljio-ink leading-relaxed">
                <p className="font-medium">{order.customer?.name}</p>
                <p className="text-welljio-muted">{addr.line1}</p>
                {addr.line2 && <p className="text-welljio-muted">{addr.line2}</p>}
                <p className="text-welljio-muted">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
              </address>
            ) : (
              <p className="text-sm text-welljio-muted">—</p>
            )}
          </div>
        </div>

        {/* Line items */}
        <div className="px-10 py-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-welljio-subtle font-semibold border-b border-slate-200">
                <th className="pb-3">Description</th>
                <th className="pb-3 text-center">Qty</th>
                <th className="pb-3 text-right">Unit price</th>
                <th className="pb-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`invoice-line-${i}`}>
                  <td className="py-4">
                    <p className="font-medium text-welljio-ink">{it.name}</p>
                  </td>
                  <td className="py-4 text-center text-welljio-muted">{it.quantity}</td>
                  <td className="py-4 text-right text-welljio-muted">{inr(it.price)}</td>
                  <td className="py-4 text-right font-medium">{inr(it.line_total || it.price * it.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-full sm:w-72 space-y-2 text-sm">
              <div className="flex justify-between text-welljio-muted">
                <span>Subtotal</span>
                <span>{inr(order.subtotal || 0)}</span>
              </div>
              <div className="flex justify-between text-welljio-muted">
                <span>Shipping</span>
                <span>{order.shipping ? inr(order.shipping) : "Free"}</span>
              </div>
              <div className="flex justify-between text-welljio-muted text-xs pb-2">
                <span>GST (inclusive)</span>
                <span>{inr(((order.subtotal || 0) * 0.18) / 1.18)}</span>
              </div>
              <div className="flex justify-between font-semibold text-base text-welljio-ink pt-3 border-t border-slate-200">
                <span>Total ({order.currency || "INR"})</span>
                <span data-testid="invoice-total">{inr(order.total || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment footer */}
        <div className="px-10 py-6 bg-welljio-tint/50 border-t border-slate-100 grid sm:grid-cols-3 gap-4 text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-welljio-subtle font-semibold mb-1.5">Payment</p>
            <p className="text-welljio-ink font-medium">{paymentLabel}</p>
            {order.razorpay_payment_id && (
              <p className="text-welljio-muted mt-1 font-mono text-[10px] break-all">{order.razorpay_payment_id}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-welljio-subtle font-semibold mb-1.5">Order Status</p>
            <p className="text-welljio-ink font-medium capitalize">
              {({ placed: "pending", confirmed: "processing", out_for_delivery: "shipped" }[order.tracking_status]) || order.tracking_status || "pending"}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-welljio-subtle font-semibold mb-1.5">Support</p>
            <p className="text-welljio-ink">support@welljio.com</p>
          </div>
        </div>

        {/* Thank you note */}
        <div className="px-10 py-6 text-center text-[11px] text-welljio-muted border-t border-slate-100 leading-relaxed">
          Thank you for choosing Welljio — physiotherapist-engineered wellness essentials.<br />
          This is a system-generated invoice and does not require a signature.
        </div>
      </div>
    </div>
  );
}
